import * as React from 'react'

import { Trash2 } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { NOOP } from '~/client/src/shared/utils/noop'
import { toast } from '~/client/src/shared/utils/toast'

import { IResourceFormRow } from './types'

interface IProps {
  selectedRecordId: string | null
  selectedRows: readonly IResourceFormRow[]
  filteredCount: number
  clearSelection: () => void
  selectRecord: (id: string | null) => void
  resourceSchema: ResourceSchema
}

function isSystemSeededRecord(row: IResourceFormRow): boolean {
  const createdBy = row.createdBy
  return typeof createdBy !== 'string' || createdBy.trim().length === 0
}

export default function ResourceFormsActionBar({
  selectedRecordId,
  selectedRows,
  filteredCount,
  clearSelection,
  selectRecord,
  resourceSchema,
}: IProps): React.ReactElement {
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const selectedHasSystemSeeded = selectedRows.some(isSystemSeededRecord)
  const selectedCount = selectedRows.length

  const handleDelete = React.useCallback(async () => {
    setIsDeleting(true)
    const idsToDelete = selectedRows.map(row => row.id)
    try {
      await resourceSchema.deleteForms(idsToDelete)
      if (selectedRecordId && idsToDelete.includes(selectedRecordId)) {
        selectRecord(null)
      }
      clearSelection()
      setIsConfirmOpen(false)
    } catch {
      toast.error(Localization.translator.failedToDeleteFormsTryAgain)
    } finally {
      setIsDeleting(false)
    }
  }, [
    clearSelection,
    resourceSchema,
    selectRecord,
    selectedRecordId,
    selectedRows,
  ])

  return (
    <div className="tw:flex tw:items-center tw:gap-2 tw:justify-between tw:min-h-11 tw:py-1.5 tw:px-3 tw:text-body-md tw:text-foreground tw:border-0 tw:border-b tw:border-solid tw:border-border">
      <div className="tw:flex tw:items-center tw:gap-3">
        <span className="tw:text-label-lg tw:text-muted-foreground tw:whitespace-nowrap">
          {selectedCount > 0
            ? Localization.translator.xOfYSelected(selectedCount, filteredCount)
            : Localization.translator.xForms(filteredCount)}
        </span>
        {selectedCount > 0 && (
          <>
            <div className="tw:border-0 tw:border-l tw:border-solid tw:border-border tw:h-7 tw:grow-0" />
            <TooltipWrapper
              content={Localization.translator.cannotDeleteSystemFormsTooltip}
              disabled={!selectedHasSystemSeeded}
            >
              <Button
                variant="destructive-ghost"
                disabled={selectedHasSystemSeeded}
                onClick={() => setIsConfirmOpen(true)}
              >
                <Trash2 />
                {Localization.translator.delete}
              </Button>
            </TooltipWrapper>
          </>
        )}
      </div>
      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.deleteSelected}
        isOpen={isConfirmOpen}
        loading={isDeleting}
        onCancelClicked={isDeleting ? NOOP : () => setIsConfirmOpen(false)}
        onDoneClicked={handleDelete}
        doneTitle={Localization.translator.delete}
      >
        <div className="tw:text-body-md tw:whitespace-pre-line tw:pb-2.5">
          {Localization.translator.deleteFormsConfirm(selectedCount)}
        </div>
      </ConfirmDialog>
    </div>
  )
}
